// ---------------------------------------------------------------------------
// ตัวเชื่อมกับฐานข้อมูล Google Sheets (Apps Script Web App)
// ---------------------------------------------------------------------------
// ใช้เก็บข้อมูลโมดูล CRM ทั้งหมด ส่วน auth / QR payment ยังยิง PaymentBroker เหมือนเดิม
// (แต่ mirror ผลลัพธ์มาเก็บที่ Sheets ด้วย — ดู gs/README.md)
//
// ถ้าไม่ได้ตั้ง REACT_APP_GS_URL ระบบจะกลับไปใช้ localStorage อัตโนมัติ
//
// ลำดับการเลือกฐานข้อมูล: Firebase RTDB (REACT_APP_FIREBASE_URL) > Google Sheets > localStorage
// เมื่อเปิด Firebase, gsGet / gsPost จะส่งต่อไป fbActions (signature เดิม) — โค้ดที่เรียกไม่ต้องแก้
// ---------------------------------------------------------------------------
import { fbEnabled } from "./fbClient";
import { fbHandle } from "./fbActions";

export const GS_URL = process.env.REACT_APP_GS_URL || "";
export const GS_KEY = process.env.REACT_APP_GS_KEY || "";

/** เปิดใช้ฐานข้อมูลบนคลาวด์หรือยัง (Firebase หรือ Google Sheets) — ชื่อเดิมคงไว้เพื่อไม่ต้องแก้ผู้เรียก */
export const gsEnabled = () => fbEnabled() || !!GS_URL;

const buildQuery = (params = {}) => {
  const entries = Object.entries({ key: GS_KEY, ...params }).filter(
    ([, v]) => v !== undefined && v !== null && v !== ""
  );
  return entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(typeof v === "object" ? JSON.stringify(v) : v)}`).join("&");
};

const unwrap = async (response) => {
  const text = await response.text();
  let payload;
  try {
    payload = JSON.parse(text);
  } catch (e) {
    throw new Error("Google Sheets ตอบกลับไม่ใช่ JSON (ตรวจสอบว่า deploy เป็น Web app แบบ Anyone แล้ว)");
  }
  if (!payload.status) throw new Error(payload.message || "เรียก Google Sheets ไม่สำเร็จ");
  return payload.data;
};

/** อ่านข้อมูล (GET) */
export const gsGet = async (action, params = {}) => {
  if (fbEnabled()) return fbHandle(action, params);
  const response = await fetch(`${GS_URL}?${buildQuery({ action, ...params })}`, {
    method: "GET",
    redirect: "follow",
  });
  return unwrap(response);
};

/**
 * เขียนข้อมูล (POST)
 * ใช้ Content-Type: text/plain เพื่อให้เป็น simple request — เลี่ยง CORS preflight
 * ที่ Apps Script ไม่รองรับ (ฝั่ง .gs อ่านจาก e.postData.contents อยู่แล้ว)
 */
export const gsPost = async (action, body = {}) => {
  if (fbEnabled()) return fbHandle(action, body);
  const response = await fetch(GS_URL, {
    method: "POST",
    redirect: "follow",
    headers: { "Content-Type": "text/plain;charset=utf-8" },
    body: JSON.stringify({ key: GS_KEY, action, ...body }),
  });
  return unwrap(response);
};

/** เช็คว่าเชื่อมต่อได้ไหม (ใช้ในหน้าตั้งค่า) */
export const gsPing = async () => {
  if (!gsEnabled()) throw new Error("ยังไม่ได้ตั้งค่า REACT_APP_FIREBASE_URL หรือ REACT_APP_GS_URL");
  return gsGet("ping");
};

// --------------------------------------------------------------------------
// ชื่อตาราง: ฝั่งหน้าเว็บใช้ camelCase ส่วนชีตใช้ snake_case
// --------------------------------------------------------------------------
export const TABLE_NAME = {
  organizations: "organizations",
  subscriptions: "subscriptions",
  roles: "roles",
  permissionGroups: "permission_groups",
  notificationSettings: "settings",
  branches: "branches",
  staff: "staff",
  customers: "customers",
  leads: "leads",
  products: "products",
  resources: "resources",
  assets: "assets",
  inventory: "inventory",
  stockMoves: "stock_moves",
  appointments: "appointments",
  tiers: "membership_tiers",
  rewards: "rewards",
  pointTransactions: "point_transactions",
  customerPackages: "customer_packages",
  chatThreads: "chat_threads",
  chatMessages: "chat_messages",
  slips: "slips",
  campaigns: "campaigns",
  salesOrders: "sales_orders",
  posSales: "pos_sales",
  quotations: "quotations",
  invoices: "invoices",
  receipts: "receipts",
  paymentLinks: "payment_links",
  contracts: "contracts",
  installmentPlans: "installment_plans",
  commissions: "commissions",
  fieldJobs: "field_jobs",
  documents: "documents",
  notifications: "notifications",
  activities: "activities",
  wallets: "wallets",
  faqs: "faqs",
  transactions: "transactions",
  boostOrders: "boost_orders",
  boostPoints: "boost_points",
  addonPurchases: "addon_purchases",
  customRequests: "custom_requests",
  taxDocuments: "tax_documents",
  docSequences: "doc_sequences",
  orgSettings: "settings",
  users: "users",
};

export const sheetOf = (collection) => TABLE_NAME[collection] || collection;

/**
 * ตารางที่ไม่ผูกกับองค์กร (makeRepo ไม่ส่ง org ให้)
 * หมายเหตุ: roles กับ settings (notification) มีแถวของแต่ละร้านปนอยู่ด้วย (มี org_id) —
 * roleRepo / settingRepo อ่าน-เขียนผ่านโค้ดเฉพาะที่ส่ง org เอง (Code.gs: HYBRID_TABLES)
 * ห้ามใช้ roleRepo.base list โดยไม่ส่ง org เพราะจะได้ข้อมูลของทุกร้าน
 */
export const GLOBAL_COLLECTIONS = ["organizations", "subscriptions", "roles", "permissionGroups", "notificationSettings", "users"];
